import { z } from "zod"

export const fibeRelationSchema = z.enum([
	"SELF",
	"FATHER",
	"MOTHER",
	"GUARDIAN",
	"SPOUSE",
	"OTHER",
])

export const avanseRelationSchema = z.enum([
	"SELF",
	"FATHER",
	"MOTHER",
	"BROTHER",
	"SISTER",
	"SPOUSE",
])

export const propelldRelationSchema = z.enum([
	"SELF",
	"FATHER",
	"MOTHER",
	"GUARDIAN",
	"SPOUSE",
	"OTHER",
])

export const ACCEPTED_MIME_TYPES = [
	"image/jpeg",
	"image/png",
	"application/pdf",
]

export const maritalStatusSchema = z.enum(["SINGLE", "MARRIED"])

export const employmentTypeSchema = z.enum(["SALARIED", "SELF-EMPLOYED"])

export const paymentModeSchema = z.enum([
	"NEFT",
	"RTGS",
	"IMPS",
	"UPI",
	"CHALLAN",
	"CASH_DEPOSIT",
	"DD",
	"CHEQUE",
])

export const requiredReferenceStatuses = ["NEFT", "RTGS", "IMPS", "UPI"]

const sharedFieldSchema = z.object({
	student_first_name: z.string().min(1, "Minimum one character is required"),
	student_last_name: z.string().min(1, "Minimum one character is required"),
	student_dob: z.string().min(1, "Date of birth is required"),
	applicant_first_name: z
		.string()
		.min(1, "Minimum one character is required"),
	applicant_last_name: z.string().min(1, "Minimum one character is required"),
	applicant_full_name: z.string().min(1, "Minimum one character is required"),
	applicant_email: z.string().email("Invalid email address"),
	applicant_dob: z.string().min(1, "Date of birth is required"),
	applicant_phone_number: z
		.string()
		.regex(/^\d{10}$/, "Invalid phone number")
		.min(1, "Minimum one character is required")
		.max(10, "Maximum 10 characters are required"),
	applicant_pan_number: z.string().min(1, "Pan number is required"),
	applicant_marital_status: z.union([maritalStatusSchema, z.string()]),
	applicant_employment_type: z.union([employmentTypeSchema, z.string()]),
	applicant_monthly_income: z.number().min(1, "Monthly income is required"),
	applicant_pincode: z.string().min(1, "Pincode is required"),
	country_code: z.string().min(1, "Country code is required"),
	otp_verified: z.boolean().default(false),
})

export const fibeRequiredFieldsSchema = sharedFieldSchema.extend({
	// otp_verified: z.boolean().default(false),
})

export const avanseRequiredFieldsSchema = sharedFieldSchema.extend({
	// applicant_name: z.string().min(1, "Minimum one character is required"),
})

const fibeSchema = z.object({
	loan_provider_name: z.literal("FIBE"),
	applicant_relation: z.union([fibeRelationSchema, z.string()]),
})

const avanseSchema = z.object({
	loan_provider_name: z.literal("AVANSE"),
	applicant_relation: z.union([avanseRelationSchema, z.string()]),
})

const propelldSchema = z.object({
	loan_provider_name: z.literal("PROPELLD"),
	applicant_relation: z.union([propelldRelationSchema, z.string()]),
})

export const combinedSchema = z
	.discriminatedUnion("loan_provider_name", [
		fibeSchema,
		avanseSchema,
		propelldSchema,
	])
	.and(
		z.object({
			loan_provider_id: z.string().min(1, "Loan provider is required"),
			loan_provider_tenure: z.number().min(1, "Loan tenure is required"),
		}),
	)
	.and(sharedFieldSchema)
	.superRefine((data, ctx) => {
		if (data.loan_provider_name === "FIBE" && !data.otp_verified) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["otp_verified"],
				message: "OTP verification is required",
			})
		}
	})

// Offline Payment Schemas
// Self
const staticFieldsSchema = z.object({
	payment_date: z.string().min(1, "Payment date is required"),
	account_holder_name: z
		.string()
		.min(1, "Account holder name is required")
		.min(3, "Account holder name must be at least 3 characters long")
		.regex(
			/^[a-zA-Z\s]+$/,
			"Account holder name must contain only letters",
		),
	bank_name: z
		.string()
		.min(1, "Bank name is required")
		.min(3, "Bank name must be at least 3 characters long")
		.regex(/^[a-zA-Z\s]+$/, "Bank name must contain only letters"),
	proof_of_payment: z
		.instanceof(File, { message: "Proof of payment is required" })
		.refine(
			(file: File) => ACCEPTED_MIME_TYPES.includes(file.type),
			"Invalid file type",
		)
		.refine(file => file.size <= 5 * 1024 * 1024, {
			message: "File size should not exceed 5MB",
		}),
	remarks: z.string().optional(),
})

const modeConditionalSchema = z
	.object({
		payment_mode: z.union([
			paymentModeSchema,
			z.string().min(1, "Payment Mode is required"),
		]),
		transaction_reference: z.string().optional(),
	})
	.superRefine((data, ctx) => {
		const isReferenceRequired = requiredReferenceStatuses.includes(
			data.payment_mode,
		)

		const isReferenceEmpty =
			!data.transaction_reference ||
			data.transaction_reference.trim() === ""

		if (isReferenceRequired && isReferenceEmpty) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["transaction_reference"],
				message: `Transaction Reference is required for ${data.payment_mode}`,
			})
		}
	})

const amountConditionalSchema = z
	.object({
		final_payable_amount: z.number().optional(),
		amount_paid: z.coerce.number().min(1, "Amount paid is required"),
	})
	.superRefine((data, ctx) => {
		if (data.amount_paid < (data.final_payable_amount || 0)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["amount_paid"],
				message: `Amount Paid is less than final payable amount`,
			})
		}
	})

export const offlinePaymentBaseSchema = staticFieldsSchema.and(
	amountConditionalSchema,
)

export const offlinePaymentSchema = offlinePaymentBaseSchema.and(
	modeConditionalSchema,
)
// Loan Offline Payment Schema
export const loanOfflinePaymentSchema = z
	.object({
		final_payable_amount: z.number().optional(),
		requested_loan_amount: z.coerce
			.number()
			.min(1, "Amount paid is required"),
	})
	.superRefine((data, ctx) => {
		if (data.requested_loan_amount < (data.final_payable_amount || 0)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["requested_loan_amount"],
				message: `Requested Loan Amount is less than final payable amount`,
			})
		}
	})
	.and(
		z.object({
			loan_id: z
				.string()
				.regex(
					/^[a-zA-Z0-9-]+$/,
					"Invalid loan ID. Only letters, numbers, and hyphens are allowed",
				)
				.min(1, "Required"),
			loan_vendor: z.string().min(1, "Required"),
			applicant_relation: z.union([
				fibeRelationSchema,
				z.string().min(1, "Required"),
			]),
			applicant_name: z.string().min(1, "Required"),
			final_payable_amount: z.number().optional(),
			applicant_mobile: z.coerce
				.number()
				.min(1000000000, "Must be a valid 10-digit number") // Minimum 10-digit number
				.max(9999999999, "Must be a valid 10-digit number"), // Maximum 10-digit number,
			applicant_email: z.string().email(),
			proof_of_loan_disbursal: z
				.instanceof(File, {
					message: "Proof of loan disbursal is required",
				})
				.refine(
					(file: File) => ACCEPTED_MIME_TYPES.includes(file.type),
					"Invalid file type",
				)
				.refine(file => file.size <= 5 * 1024 * 1024, {
					message: "File size should not exceed 5MB",
				}),
			remarks: z.string().optional(),
		}),
	)

export type OfflinePaymentFormData = z.infer<typeof offlinePaymentSchema>
export type paymentModeSchemaType = z.infer<typeof paymentModeSchema>
